/**
 * ========================================
 * CUSTOM ASSET - lens-journey-modded.js
 * Version: 2.1
 * Created: 2026-10-20
 * Last Modified: 2026-10-25
 * Client: Studio Optix
 * ========================================
 *
 * PURPOSE:
 * <lens-journey-modded> custom element (lens-journey-modded.liquid):
 * the Warby Parker-style lens selection drawer. Handles open/close,
 * step navigation with conditional branching, progress bar, running
 * subtotal (frame price + selected options), the review screen, and
 * the final add to cart:
 *   1. add-on products (options with data-variant-id) are POSTed to
 *      /cart/add.js, tagged "_Paired with: <frame title>";
 *   2. hidden properties[...] inputs are injected into the stock
 *      buy-buttons form (data-form-id) and its own submit button is
 *      clicked, so the theme's native cart flow (drawer refresh,
 *      events, animations) runs for the frame itself.
 * Branching (data-show-if on steps): comma = OR, "&" = AND between
 * groups - "transitions & zeiss-sv,zeiss-progressive" shows the step
 * only when transitions AND one of the Zeiss designs were selected on
 * earlier visible steps. Steps with data-multi are multi-select:
 * option cards toggle, a Continue button (data-lens-continue)
 * advances; every selected option is recorded and priced.
 * With data-hide-stock, the stock add-to-cart button is visually
 * hidden but kept in the DOM for that final submit. In the theme
 * editor, selecting the block (or a step inside it) opens the drawer
 * and jumps to that step.
 *
 * MODIFICATIONS:
 * - v2.1 (2026-10-25): [data-buy-as-is] options (the "Frame as is"
 *   card on the unified first step) short-circuit the journey: the
 *   bare frame is submitted through the stock form right away with a
 *   visible "<step property>: <option title>" note, no pairing or
 *   lens lines, and the drawer closes. Part of the single
 *   "Select Lenses and Buy" CTA (Chris, Oct 25).
 * - v2.0 (2026-10-21): data-implies entries ride ON THE CHOOSING
 *   STEP's selection list (not the hidden step's) so they price, feed
 *   branching values, and clear automatically when the choice changes.
 * - v1.9 (2026-10-21): data-implies support - an option can auto-select
 *   options on other steps by value.
 * - v1.8 (2026-10-20): Pair numbering - the frame line gets a visible
 *   "Pair: N" and EVERY lens component line gets "For frame: <title>
 *   - Pair N" (N = existing lens groups in cart + 1), so two frames
 *   with lenses stay identifiable line by line. Extras (the care kit)
 *   no longer carry any pairing - they are independent add-ons.
 * - v1.7 (2026-10-20): Priced selections are no longer duplicated into
 *   the lens-line notes - their own cart line is the record (a removed
 *   or refunded line can't contradict a stale note). Notes now carry
 *   only uncharged data: For frame, prescription status, Included
 *   design/material, lens color.
 * - v1.6 (2026-10-20): Visible "For frame: <title>" note on the base
 *   lens line, so multiple frame+lens purchases stay unambiguous.
 * - v1.5 (2026-10-20): The visible selection notes (Prescription type,
 *   Lens design, treatments, color...) now ride on the BASE LENS line
 *   (the lens-rx-* add-on) instead of the frame - they describe the
 *   lenses. The frame keeps only the hidden _Lens group pairing tag;
 *   if a build has no lens product, notes fall back to the frame.
 * - v1.4 (2026-10-20): Frame and add-on lines share a "_Lens group"
 *   property so cart-guard-modded.js can lock lens lines and cascade
 *   their removal with the frame.
 * - v1.3 (2026-10-20): Scroll lock via the theme's html[scroll-lock]
 *   attribute - on desktop Horizon scrolls .page-wrapper, not body, so
 *   the old body:has() overflow rule never locked anything.
 * - v1.2 (2026-10-20): Adam's lens menu support - multi-select steps
 *   (treatments), "&" AND-syntax in data-show-if (brand-dependent
 *   photochromic color steps), per-step selections become arrays,
 *   properties with the same label join with ", ".
 * - v1.1 (2026-10-20): Drawer + scrim are portaled to <body> on connect
 *   (and removed on disconnect) - ancestor transforms in the section
 *   were trapping position:fixed, clipping the drawer under the header.
 * - v1.0 (2026-10-20): Initial creation.
 */

class LensJourneyModded extends HTMLElement {
  /** selections per step element: Array<{ label, title, value, cents, display, variantId }> */
  #selections = new Map();
  #index = 0;
  #scrim = null;
  #drawer = null;

  connectedCallback() {
    // portal the drawer out of the section: ancestor transforms (scroll
    // animations) otherwise trap position:fixed and clip the drawer
    this.#scrim = this.querySelector('[ref="scrim"]');
    this.#drawer = this.querySelector('[ref="drawer"]');
    if (this.#scrim) document.body.append(this.#scrim);
    if (this.#drawer) document.body.append(this.#drawer);

    this.#ref('open')?.addEventListener('click', () => this.open());
    this.#ref('close')?.addEventListener('click', () => this.close());
    this.#scrim?.addEventListener('click', () => this.close());
    this.#ref('back')?.addEventListener('click', () => this.#back());
    this.#ref('add')?.addEventListener('click', () => this.#addToCart());

    for (const step of this.#steps) {
      for (const option of step.querySelectorAll('[data-lens-option]')) {
        option.addEventListener('click', () => this.#choose(step, option));
      }
      step.querySelector('[data-lens-skip]')?.addEventListener('click', () => this.#skip(step));
      step.querySelector('[data-lens-continue]')?.addEventListener('click', () => this.#advance());
    }

    this.#drawer?.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });

    if (this.hasAttribute('data-hide-stock')) this.#hideStockButton();

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', (e) => {
        if (!this.contains(e.target) && !this.#drawer?.contains(e.target)) return;
        this.open();
        const step = e.target.closest?.('[data-lens-step]') || e.target.querySelector?.('[data-lens-step]');
        if (step) this.#jumpTo(step);
      });
      document.addEventListener('shopify:block:deselect', (e) => {
        if (e.target === this) this.close();
      });
    }
  }

  disconnectedCallback() {
    if (this.#drawer?.hasAttribute('data-open')) document.documentElement.removeAttribute('scroll-lock');
    this.#scrim?.remove();
    this.#drawer?.remove();
  }

  get #steps() {
    return this.#drawer ? [...this.#drawer.querySelectorAll('[data-lens-step]')] : [];
  }

  #ref(name) {
    if (name === 'scrim') return this.#scrim;
    if (name === 'drawer') return this.#drawer;
    if (name === 'open') return this.querySelector('[ref="open"]');
    return this.#drawer?.querySelector(`[ref="${name}"]`);
  }

  /**
   * steps whose data-show-if matches selections on earlier visible steps.
   * Syntax: "a,b & c" = (a OR b) AND c.
   */
  get #visibleSteps() {
    const visible = [];
    const values = new Set();
    for (const step of this.#steps) {
      const condition = (step.dataset.showIf || '').trim();
      const shown =
        !condition ||
        condition
          .split('&')
          .every((group) => group.split(',').some((v) => values.has(v.trim())));
      if (!shown) continue;
      visible.push(step);
      for (const chosen of this.#selections.get(step) || []) values.add(chosen.value);
    }
    return visible;
  }

  /** selections on currently-visible steps only, flattened in step order */
  get #activeSelections() {
    return this.#visibleSteps.flatMap((step) => this.#selections.get(step) || []);
  }

  open() {
    this.#index = 0;
    document.documentElement.setAttribute('scroll-lock', '');
    this.#scrim?.removeAttribute('hidden');
    requestAnimationFrame(() => {
      this.#scrim?.setAttribute('data-open', '');
      this.#drawer?.setAttribute('data-open', '');
    });
    this.#render();
  }

  close() {
    document.documentElement.removeAttribute('scroll-lock');
    this.#drawer?.removeAttribute('data-open');
    if (this.#scrim) {
      this.#scrim.removeAttribute('data-open');
      const scrim = this.#scrim;
      setTimeout(() => scrim.setAttribute('hidden', ''), 300);
    }
  }

  #optionData(step, option) {
    return {
      label: step.dataset.property || '',
      title: option.dataset.title,
      value: option.dataset.value,
      cents: parseInt(option.dataset.priceCents, 10) || 0,
      display: option.dataset.priceDisplay || '',
      variantId: option.dataset.variantId || null,
      handle: option.dataset.productHandle || null,
    };
  }

  /** options on OTHER steps that this option auto-selects, carrying
   *  their own step's label/price - stored on the CHOOSING step so they
   *  count while their own step is skipped */
  #impliedEntries(option) {
    const out = [];
    const implied = (option.dataset.implies || '').split(',').map((v) => v.trim()).filter(Boolean);
    for (const value of implied) {
      for (const other of this.#steps) {
        const match = other.querySelector(`[data-lens-option][data-value="${value}"]`);
        if (match) {
          out.push(this.#optionData(other, match));
          break;
        }
      }
    }
    return out;
  }

  #choose(step, option) {
    if (option.hasAttribute('data-buy-as-is')) {
      this.#buyAsIs(step, option);
      return;
    }
    const impliedValues = (option.dataset.implies || '').split(',').map((v) => v.trim()).filter(Boolean);
    if (step.hasAttribute('data-multi')) {
      const current = this.#selections.get(step) || [];
      const existing = current.findIndex((s) => s.value === option.dataset.value);
      if (existing >= 0) {
        this.#selections.set(step, current.filter((s) => s.value !== option.dataset.value && !impliedValues.includes(s.value)));
      } else {
        current.push(this.#optionData(step, option), ...this.#impliedEntries(option).filter((e) => !current.some((s) => s.value === e.value)));
        this.#selections.set(step, current);
      }
      this.#render(); // stay on the step; toggle highlight + subtotal
      return;
    }
    this.#selections.set(step, [this.#optionData(step, option), ...this.#impliedEntries(option)]);
    this.#advance();
  }

  #skip(step) {
    this.#selections.delete(step);
    this.#advance();
  }

  #advance() {
    const total = this.#visibleSteps.length;
    this.#index = Math.min(this.#index + 1, total); // total == review screen
    this.#render();
  }

  #back() {
    this.#index = Math.max(this.#index - 1, 0);
    this.#render();
  }

  #jumpTo(step) {
    const i = this.#visibleSteps.indexOf(step);
    if (i >= 0) {
      this.#index = i;
    } else {
      // editor preview of a conditionally-hidden step: show it raw
      for (const s of this.#steps) s.setAttribute('hidden', '');
      this.#ref('review')?.setAttribute('hidden', '');
      step.removeAttribute('hidden');
      return;
    }
    this.#render();
  }

  #render() {
    const visible = this.#visibleSteps;
    const onReview = this.#index >= visible.length;

    for (const step of this.#steps) step.setAttribute('hidden', '');
    this.#ref('review')?.setAttribute('hidden', '');

    if (onReview) {
      this.#renderReview();
      this.#ref('review')?.removeAttribute('hidden');
    } else {
      const step = visible[this.#index];
      const chosen = this.#selections.get(step) || [];
      for (const option of step.querySelectorAll('[data-lens-option]')) {
        option.setAttribute(
          'aria-pressed',
          chosen.some((s) => s.value === option.dataset.value) ? 'true' : 'false'
        );
      }
      step.removeAttribute('hidden');
    }

    const backBtn = this.#ref('back');
    if (backBtn) backBtn.toggleAttribute('hidden', this.#index === 0);

    const addBtn = this.#ref('add');
    if (addBtn) addBtn.toggleAttribute('hidden', !onReview);

    const progress = this.#ref('progress');
    if (progress) {
      const pct = ((this.#index + 1) / (visible.length + 1)) * 100;
      progress.style.width = `${Math.min(pct, 100)}%`;
    }

    const subtotal = this.#ref('subtotal');
    if (subtotal) subtotal.textContent = this.#money(this.#subtotalCents());

    const error = this.#ref('error');
    if (error) error.setAttribute('hidden', '');

    this.#drawer?.querySelector('.lens-journey-modded__body')?.scrollTo(0, 0);
  }

  #subtotalCents() {
    const frame = parseInt(this.dataset.framePrice, 10) || 0;
    return this.#activeSelections.reduce((sum, s) => sum + s.cents, frame);
  }

  #renderReview() {
    const list = this.#ref('summary');
    if (!list) return;
    list.innerHTML = '';

    const addRow = (name, detail, price) => {
      const li = document.createElement('li');
      const left = document.createElement('span');
      const strong = document.createElement('strong');
      strong.textContent = name;
      left.append(strong);
      if (detail) {
        const small = document.createElement('small');
        small.textContent = detail;
        left.append(small);
      }
      const right = document.createElement('span');
      right.textContent = price;
      li.append(left, right);
      list.append(li);
    };

    addRow(
      this.dataset.productTitle || 'Frame',
      '',
      this.#money(parseInt(this.dataset.framePrice, 10) || 0)
    );
    for (const s of this.#activeSelections) {
      addRow(s.label || s.title, s.label ? s.title : '', s.display.trim() || '—');
    }
  }

  #money(cents) {
    const symbol = this.dataset.currency || '$';
    const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return `${symbol}${amount}`;
  }

  #hideStockButton() {
    const form = document.getElementById(this.dataset.formId);
    const stock = form?.querySelector('add-to-cart-component');
    if (stock) stock.style.display = 'none';
  }

  /** "Frame as is": submit the bare frame through the stock form with a
   *  visible note, no lens lines or pairing, and close the drawer. */
  #buyAsIs(step, option) {
    const form = document.getElementById(this.dataset.formId);
    const error = this.#ref('error');
    if (!form) {
      if (error) {
        error.textContent = 'Add to cart form not found on this page.';
        error.removeAttribute('hidden');
      }
      return;
    }
    form.querySelectorAll('[data-lens-journey-prop]').forEach((el) => el.remove());
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${step.dataset.property || 'Lenses'}]`;
    input.value = option.dataset.title || 'As is';
    input.setAttribute('data-lens-journey-prop', '');
    form.append(input);

    const submit = form.querySelector('button[type="submit"], [type="submit"]');
    if (submit) {
      submit.click();
    } else {
      form.requestSubmit();
    }
    this.close();
  }

  async #addToCart() {
    const form = document.getElementById(this.dataset.formId);
    const error = this.#ref('error');
    if (!form) {
      if (error) {
        error.textContent = 'Add to cart form not found on this page.';
        error.removeAttribute('hidden');
      }
      return;
    }

    const addBtn = this.#ref('add');
    if (addBtn) addBtn.setAttribute('disabled', '');
    const lensGroup = `LJ-${Date.now().toString(36)}`;

    try {
      // pair number: one higher than the lens groups already in the cart
      let pairNumber = 1;
      try {
        const existing = await fetch('/cart.js').then((r) => r.json());
        const groups = new Set();
        for (const item of existing.items) {
          const g = item.properties?.['_Lens group'];
          if (g) groups.add(g);
        }
        pairNumber = groups.size + 1;
      } catch (e) {
        /* cart unreadable: default to 1 */
      }
      const frameTag = `${this.dataset.productTitle || 'Frame'} — Pair ${pairNumber}`;
      const isLensComponent = (s) => ['lens-rx-', 'lens-design-', 'lens-material-', 'lens-treatment-'].some((p) => s.handle?.startsWith(p));

      // the visible notes: uncharged selections only - anything with its
      // own cart line (variantId) is documented by that line, so a later
      // line removal or refund can't contradict a stale note
      const notes = {};
      for (const s of this.#activeSelections) {
        if (!s.label || s.variantId) continue;
        notes[s.label] = notes[s.label] ? `${notes[s.label]}, ${s.title}` : s.title;
      }

      // 1. add-on products first (silently), so the frame's native add
      // refreshes the cart drawer with everything in it. The notes ride
      // on the BASE LENS line (lens-rx-*) - they describe the lenses.
      const addons = this.#activeSelections.filter((s) => s.variantId);
      const baseLens = addons.find((s) => s.handle?.startsWith('lens-rx-'));
      if (addons.length) {
        const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: addons.map((s) => ({
              id: parseInt(s.variantId, 10),
              quantity: 1,
              properties: isLensComponent(s)
                ? {
                    'For frame': frameTag,
                    ...(s === baseLens ? notes : {}),
                    '_Lens group': lensGroup,
                  }
                : {},
            })),
          }),
        });
        const result = await response.json();
        if (result.status) throw new Error(result.description || result.message || 'Could not add lens options.');
      }

      // 2. the frame carries only the hidden pairing tag - unless there
      // is no lens line to hold the notes, then they fall back here
      form.querySelectorAll('[data-lens-journey-prop]').forEach((el) => el.remove());
      const properties = new Map([['Pair', String(pairNumber)], ['_Lens group', lensGroup]]);
      if (!baseLens) {
        for (const [label, value] of Object.entries(notes)) properties.set(label, value);
      }
      for (const [label, value] of properties) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${label}]`;
        input.value = value;
        input.setAttribute('data-lens-journey-prop', '');
        form.append(input);
      }

      // 3. native submit -> theme cart drawer, events, animations
      const submit = form.querySelector('button[type="submit"], [type="submit"]');
      if (submit) {
        submit.click();
      } else {
        form.requestSubmit();
      }
      this.close();
    } catch (e) {
      if (error) {
        error.textContent = e.message || 'Something went wrong - please try again.';
        error.removeAttribute('hidden');
      }
    } finally {
      if (addBtn) addBtn.removeAttribute('disabled');
    }
  }
}

if (!customElements.get('lens-journey-modded')) {
  customElements.define('lens-journey-modded', LensJourneyModded);
}
